import { ArrowLeft, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  GlassCard,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_PRIMARY } from '@/shared/ui-class';
import {
  SEED_PERSONAL_SKILLS,
  SEED_PLATFORM_SKILLS,
  SEED_SKILL_BODIES,
  type SettingsSkillSeed,
} from '@/versions/v1/shared/settings-seed';
import { PlatformSkillInfo } from './PlatformSkillInfo';

const DEFAULT_BODY = `# Skill body

Describe what the agent should do when this skill applies.`;

function findSkill(slug: string): SettingsSkillSeed | undefined {
  return (
    SEED_PERSONAL_SKILLS.find((skill) => skill.slug === slug) ??
    SEED_PLATFORM_SKILLS.find((skill) => skill.slug === slug)
  );
}

function seedBodyFor(slug: string): string {
  return SEED_SKILL_BODIES.find((entry) => entry.slug === slug)?.body ?? DEFAULT_BODY;
}

export function SkillDetailScene() {
  const { slug = '' } = useParams<{ slug: string }>();
  const skill = useMemo(() => findSkill(slug), [slug]);
  const isPersonal = skill?.source === 'user';
  const [description, setDescription] = useState(skill?.description ?? '');
  const [body, setBody] = useState(() => seedBodyFor(slug));
  const [saved, setSaved] = useState(false);

  if (!skill) {
    return (
      <SurfacePageLayout width="narrow">
        <SurfacePageBody className="space-y-4">
          <p className="text-sm text-muted">Skill not found.</p>
          <Link
            to="/settings/skills"
            className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to skills
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const handleSave = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="SkillDetailScene">
        <SurfacePageBody className="space-y-4" as="div">
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/settings/skills"
              className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to skills
            </Link>
            {isPersonal ? (
              <button
                type="button"
                onClick={handleSave}
                className={`${BUTTON_PRIMARY} gap-1.5`}
              >
                <Save className="h-4 w-4" />
                {saved ? 'Saved' : 'Save'}
              </button>
            ) : null}
          </div>

          <GlassCard className="flex flex-col gap-3 p-4 shadow-sm">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted">
                Slug
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {skill.slug}
                </span>
                {skill.source === 'platform' ? (
                  <span className="inline-flex items-center gap-1">
                    <Pill>Platform</Pill>
                    <PlatformSkillInfo skillSlug={skill.slug} />
                  </span>
                ) : (
                  <Pill>Personal</Pill>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="skill-description"
                className="text-xs font-medium uppercase tracking-wide text-muted"
              >
                Description
              </label>
              {isPersonal ? (
                <input
                  id="skill-description"
                  type="text"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  className="w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
                />
              ) : (
                <p className="text-sm text-zinc-700 dark:text-zinc-300">
                  {skill.description}
                </p>
              )}
            </div>
          </GlassCard>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="skill-body"
              className="text-xs font-medium uppercase tracking-wide text-muted"
            >
              Body (markdown)
            </label>
            {isPersonal ? (
              <textarea
                id="skill-body"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={16}
                className="w-full rounded-xl border border-divider bg-input px-3 py-2 font-mono text-sm outline-none focus:border-input-focus"
              />
            ) : (
              <GlassCard className="max-h-[60vh] overflow-y-auto p-4">
                <pre className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-900 dark:text-zinc-100">
                  {body}
                </pre>
              </GlassCard>
            )}
          </div>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
